import { useState } from "react";
import { motion } from "framer-motion";
import { personalInfo, skills } from "../data/personalInfo";
import SectionTitle from "./SectionTitle";
import { Mail, MapPin, Copy, Check, Terminal, Cpu, Layout, Cloud } from "lucide-react";

const About = () => {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(personalInfo.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const categories = [
    {
      title: "Mobile Development",
      icon: <Cpu className="w-4 h-4 text-cyan-500 mr-2" />,
      skills: skills.filter(s => ["React Native"].includes(s))
    },
    {
      title: "Frontend & Web Architectures",
      icon: <Terminal className="w-4 h-4 text-indigo-500 mr-2" />,
      skills: skills.filter(s => ["React JS", "Next JS", "TypeScript", "JavaScript", "HTML5 & CSS3"].includes(s))
    },
    {
      title: "UI Frameworks & Design",
      icon: <Layout className="w-4 h-4 text-purple-500 mr-2" />,
      skills: skills.filter(s => ["Tailwind", "Bootstrap"].includes(s))
    },
    {
      title: "Cloud & Infrastructure",
      icon: <Cloud className="w-4 h-4 text-amber-500 mr-2" />,
      skills: skills.filter(s => ["Firebase", "AWS", "Vercel"].includes(s))
    }
  ];

  const categorizedSkillNames = categories.flatMap(c => c.skills);
  const otherSkills = skills.filter(s => !categorizedSkillNames.includes(s));
  if (otherSkills.length > 0) {
    categories.push({
      title: "Other Technologies",
      icon: <Terminal className="w-4 h-4 text-pink-500 mr-2" />,
      skills: otherSkills
    });
  }

  return (
    <section id="about" className="py-24 relative overflow-hidden bg-gray-50/40 dark:bg-gray-950/30">
      {/* Background ambient lighting */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-bl-full blur-3xl opacity-50 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 rounded-tr-full blur-3xl opacity-50 pointer-events-none"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <SectionTitle 
          title="About & Technical Expertise" 
          subtitle="Transforming ideas into resilient, production-ready applications with modern web & mobile technologies" 
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12 items-stretch">
          
          {/* Column 1: Bio & Contact Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 flex flex-col justify-between glass-card p-8 rounded-3xl"
          >
            <div>
              <h3 className="text-2xl font-bold font-heading text-gray-900 dark:text-white mb-6 relative pb-3 inline-block">
                Who I Am
                <span className="absolute bottom-0 left-0 w-14 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"></span>
              </h3>
              
              <div className="space-y-4 text-gray-600 dark:text-gray-300 leading-relaxed text-base sm:text-lg">
                <p>{personalInfo.bio}</p>
                <p>
                  Based in <span className="font-semibold text-indigo-600 dark:text-indigo-400">{personalInfo.location}</span>, I build clean, high-performance user interfaces and scalable backends. I bridge the gap between design vision and technical execution.
                </p>
              </div>
            </div>
            
            {/* Contact Details & Quick Copy Email */}
            <div className="mt-8 pt-8 border-t border-gray-200/60 dark:border-gray-800 space-y-4">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                Direct Contact
              </h4>
              
              {/* Email item with copy button */}
              <div className="flex items-center justify-between p-3.5 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/40">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
                    <Mail className="h-4.5 w-4.5" />
                  </div>
                  <a
                    href={`mailto:${personalInfo.email}`}
                    className="text-gray-800 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 font-semibold transition-colors text-sm truncate"
                  >
                    {personalInfo.email}
                  </a>
                </div>
                
                <button
                  onClick={handleCopyEmail}
                  className="p-2 rounded-xl bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-all border border-gray-200 dark:border-gray-700 shrink-0 cursor-pointer"
                  title="Copy email to clipboard"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              
              {/* Location item */}
              <div className="flex items-center space-x-3.5 px-3.5 py-2">
                <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                  <MapPin className="h-4.5 w-4.5" />
                </div>
                <span className="text-gray-700 dark:text-gray-300 font-medium text-sm">
                  {personalInfo.location}
                </span>
              </div>
            </div>
          </motion.div>
          
          {/* Column 2: Technical Skills & Stack Categories */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7 flex flex-col"
          >
            <div className="glass-card p-8 rounded-3xl flex-1 flex flex-col justify-between">
              <div>
                <h3 className="text-2xl font-bold font-heading text-gray-900 dark:text-white mb-6 relative pb-3 inline-block">
                  Technical Stack & Tools
                  <span className="absolute bottom-0 left-0 w-14 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"></span>
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {categories.map((category, idx) => (
                    <motion.div 
                      key={idx}
                      whileHover={{ scale: 1.02 }}
                      transition={{ type: "spring", stiffness: 250 }}
                      className="p-5 rounded-2xl bg-white/70 dark:bg-gray-900/50 border border-gray-200/80 dark:border-gray-800/80 shadow-sm hover:shadow-md hover:border-indigo-500/40 dark:hover:border-indigo-500/30 transition-all duration-300"
                    >
                      <h4 className="font-heading text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center">
                        {category.icon}
                        {category.title}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {category.skills.map((skill, sIdx) => (
                          <span
                            key={sIdx}
                            className="px-3 py-1 rounded-lg text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200/60 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800/60 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-600 dark:hover:text-white transition-all duration-200 cursor-default shadow-2xs"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Bottom engineering note */}
              <div className="mt-8 pt-6 border-t border-gray-200/60 dark:border-gray-800 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>✦ Clean Architecture</span>
                <span>✦ Cross-Platform Native</span>
                <span>✦ Modern Web Standards</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default About;